import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { ArrowLeft, Shield, Zap, Clock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import CinematicBackdrop from '../cinematic/CinematicBackdrop';
import SplitText from '../cinematic/SplitText';
import MagneticButton from '../cinematic/MagneticButton';
import { firebaseReady, signInWithGoogleViaFirebase } from '../firebase/firebase';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { user, login, loading, refresh } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [fbSigningIn, setFbSigningIn] = useState(false);

  // Already logged in — go to dashboard
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#FF6B4A] animate-spin mx-auto mb-3" />
          <p className="text-sm text-white/50">Loading...</p>
        </div>
      </div>
    );
  }

  const handleLogin = () => {
    setSigningIn(true);
    // Small delay for smooth visual transition
    setTimeout(() => login(), 600);
  };

  const handleFirebaseLogin = async () => {
    setFbSigningIn(true);
    try {
      const { idToken } = await signInWithGoogleViaFirebase();
      await axios.post(`${API}/auth/firebase-login`, { id_token: idToken }, { withCredentials: true });
      // Auth is established via httpOnly session cookie set by the backend — no localStorage needed.
      toast.success('Welcome!');
      if (refresh) await refresh();
      setTimeout(() => { window.location.href = '/dashboard'; }, 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Sign-in failed';
      toast.error(msg);
      setFbSigningIn(false);
    }
  };

  // While signing in, show a full-screen DIS2ORT-branded splash so the OAuth
  // redirect doesn't visually intrude with another brand's chrome.
  if (signingIn) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0A0A0F]" data-testid="login-splash">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#FF6B4A]/15 blur-[120px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#E040A0]/15 blur-[120px] pointer-events-none" />
        <div className="relative text-center">
          <div className="flex items-baseline justify-center gap-0 mb-8">
            <span className="font-body text-4xl font-extrabold tracking-tight text-white uppercase">DIS</span>
            <span className="font-body text-4xl font-extrabold tracking-tight uppercase text-[#FF6B4A]">2</span>
            <span className="font-body text-4xl font-extrabold tracking-tight text-white uppercase">ORT</span>
          </div>
          <Loader2 className="w-7 h-7 text-[#FF6B4A] animate-spin mx-auto mb-4" />
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-white/60">Signing you in…</p>
          <p className="text-[10px] text-white/30 mt-2 tracking-wider">Secure sign in by Google</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex text-white relative cinematic-grain">
      <CinematicBackdrop intensity="soft" />
      {/* Left — branding panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative z-10" style={{ background: 'linear-gradient(135deg, rgba(26,26,46,0.7) 0%, rgba(42,32,64,0.6) 50%, rgba(26,26,46,0.7) 100%)', backdropFilter: 'blur(20px)' }}>
        <Link to="/" className="flex items-baseline gap-0">
          <span className="font-body text-2xl font-extrabold tracking-tight text-white uppercase">DIS</span>
          <span className="font-body text-2xl font-extrabold tracking-tight uppercase text-[#FF6B4A]">2</span>
          <span className="font-body text-2xl font-extrabold tracking-tight text-white uppercase">ORT</span>
        </Link>

        <div>
          <h2 className="font-display text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
            <SplitText text="150 AI models" className="block" stagger={0.04} duration={0.7} />
            <span>
              in <span className="italic text-[#FF6B4A]"><SplitText text="one studio." stagger={0.05} duration={0.75} delay={0.4} /></span>
            </span>
          </h2>
          <p className="text-white/40 text-base max-w-sm leading-relaxed">
            DALL·E 3, Midjourney v7, FLUX Pro, Sora 2, Kling, Runway — every major AI image &amp; video model in a single tab.
          </p>

          <div className="mt-12 space-y-4">
            {[
              { icon: Zap, text: '50 free credits on signup' },
              { icon: Clock, text: 'Jobs start processing in seconds' },
              { icon: Shield, text: 'SOC2 compliant infrastructure' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <item.icon className="w-4 h-4 text-[#FF6B4A]" />
                </div>
                <span className="text-white/60 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/20 text-xs">&copy; {new Date().getFullYear()} DIS2ORT. All rights reserved.</p>
      </div>

      {/* Right — login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10">
            <Link to="/" className="flex items-baseline gap-0">
              <span className="font-body text-2xl font-extrabold tracking-tight text-white uppercase">DIS</span>
              <span className="font-body text-2xl font-extrabold tracking-tight uppercase text-[#FF6B4A]">2</span>
              <span className="font-body text-2xl font-extrabold tracking-tight text-white uppercase">ORT</span>
            </Link>
          </div>

          <Link to="/" className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to home
          </Link>

          <h1 className="font-display text-3xl font-bold text-white mb-2" data-testid="login-heading">Welcome back</h1>
          <p className="text-sm text-white/50 mb-10">Sign in to access your rendering dashboard.</p>

          {/* PRIMARY — Firebase Google Sign In (DIS2ORT-branded, no Emergent splash) */}
          {firebaseReady && (
            <MagneticButton strength={0.2} radius={100} className="block w-full mb-3">
              <Button
                onClick={handleFirebaseLogin}
                disabled={fbSigningIn || signingIn}
                className="w-full h-12 bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] hover:from-[#FF855E] hover:to-[#F050B0] text-white rounded-xl font-bold text-sm tracking-wide shadow-[0_8px_24px_-8px_rgba(255,107,74,0.6)] transition-all flex items-center justify-center gap-3 disabled:opacity-60"
                data-testid="firebase-sign-in-btn"
              >
              {fbSigningIn ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>Signing in via Google…</span>
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#fff"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff" opacity="0.85"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff" opacity="0.7"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff" opacity="0.9"/>
                  </svg>
                  Sign in with Google · DIS2ORT
                </>
              )}
              </Button>
            </MagneticButton>
          )}

          {/* SECONDARY — legacy Emergent Auth. Hidden when Firebase is active. */}
          {!firebaseReady && (
          <MagneticButton strength={0.2} radius={100} className="block w-full">
            <Button
              onClick={handleLogin}
              disabled={signingIn || fbSigningIn}
              className={`w-full h-12 ${firebaseReady ? 'bg-white/[0.03] border border-white/[0.06]' : 'bg-white/[0.04] border border-white/[0.08]'} text-white rounded-xl font-medium text-sm hover:bg-white/[0.08] hover:border-white/[0.15] transition-all flex items-center justify-center gap-3`}
              data-testid="google-sign-in-btn"
            >
              {signingIn ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-[#8A8A9A]" />
                  <span className="text-[#8A8A9A]">Redirecting to Google...</span>
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>
          </MagneticButton>
          )}

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/[0.08]"></div></div>
            <div className="relative flex justify-center"><span className="bg-[#0A0A0F] px-3 text-xs text-white/50">Secure sign in powered by Google</span></div>
          </div>

          <div className="space-y-4 text-center">
            <p className="text-xs text-white/50 leading-relaxed">
              By signing in, you agree to our Terms of Service and Privacy Policy. Your first account includes <span className="font-semibold text-[#FF6B4A]">50 free render credits</span>.
            </p>
          </div>

          {/* Trust badges */}
          <div className="mt-12 pt-8 border-t border-white/[0.05]">
            <div className="flex items-center justify-center gap-6 text-[10px] text-white/50">
              <div className="flex items-center gap-1.5"><Shield className="w-3 h-3" /> SSL Encrypted</div>
              <div className="flex items-center gap-1.5"><Zap className="w-3 h-3" /> Instant Access</div>
              <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" /> No CC Required</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
